import {Either, getOrThrow, isLeft, left, right} from 'effect/Either';
import {isString} from 'effect/Predicate';
import React, {useCallback, useSyncExternalStore} from 'react';
import {env} from '~/environment';
import {ShellEventBus} from '~/shared/shell-bridge/ShellEventBus';
import {isIOS} from '~/shared/utils/mobileDeviceDetect';
import {ShellVersionFallback} from '../ui/ShellVersionFallback';

interface ShellVersion {
    year: number;
    major: number;
    minor: number;
}

export namespace ShellVersion {
    const levels = ['year', 'major', 'minor'] as Array<keyof ShellVersion>;

    function parse(appVersion: string): Either<ShellVersion, Error> {
        const errors: Error[] = [];
        const parts = appVersion.split('.');
        const [yearRaw, majorRaw, minorRaw] = parts;
        const [year, major, minor] = parts.map(Number);
        if (isNaN(year)) {
            errors[errors.length] = new TypeError(`appVersion.year is NaN because it's ${yearRaw}`);
        }
        if (isNaN(major)) {
            errors[errors.length] = new TypeError(`appVersion.major is NaN because it's ${majorRaw}`);
        }
        if (parts.length === 3 && isNaN(minor)) {
            errors[errors.length] = new TypeError(`appVersion.minor is NaN because it's ${minorRaw}`);
        }
        if (errors.length > 1) {
            return left(new AggregateError(errors));
        }
        if (errors.length > 0) {
            return left(errors[0]);
        }
        return right({
            year,
            major,
            minor: minor ?? 0,
        });
    }

    export function parseUnknown(maybeVersion: unknown) {
        if (typeof maybeVersion !== 'string') {
            return left(new Error('appVersion not a string'));
        }
        return parse(maybeVersion);
    }

    export function parseAssert(version: string) {
        return parse(version).pipe(getOrThrow);
    }

    export function getFromWindow(): null | ShellVersion {
        const {appVersion} = window;
        if (!isString(appVersion)) {
            return null;
        }
        const parsed = parseUnknown(window.appVersion);
        if (isLeft(parsed)) {
            reportError(parsed.left);
            return null;
        }
        return parsed.right;
    }

    /**
     * A workaround for shell injecting appVersion too late.
     */
    export function registerDescriptor() {
        const key = '__appVersion__';

        const current = window.appVersion;
        if (current) {
            Reflect.set(window, key, current);
        }

        Object.defineProperties(window, {
            appVersion: {
                get() {
                    return Reflect.get(window, key);
                },
                set(version) {
                    Reflect.set(window, key, version);
                    ShellEventBus.emulateShellEvent('ShellVersionResponse', {version});
                },
            },
        });
    }

    export const Gate = function ShellVersionGate({children}: React.PropsWithChildren) {
        useSubscription();

        if (isCrucialOutdated()) {
            return React.createElement(ShellVersionFallback);
        }
        return children;
    };

    export function useSubscription() {
        useSyncExternalStore(
            useCallback(callback => ShellEventBus.subscribe('ShellVersionResponse', callback), []),
            useCallback(() => window.appVersion, [])
        );
    }

    export function format(version: ShellVersion) {
        let v = [version.year, version.major];
        if (version.minor > 0) {
            v = [version.year, version.major, version.minor];
        }
        return v.join('.');
    }

    const operatorToFnMap = {
        '<': (a, b) => a < b,
        '<=': (a, b) => a <= b,
        '>': (a, b) => a > b,
        '>=': (a, b) => a >= b,
    } satisfies Record<string, (a: number, b: number) => boolean>;

    export function compare(a: ShellVersion | string, op: keyof typeof operatorToFnMap, b: ShellVersion | string) {
        if (typeof a === 'string') {
            a = parseAssert(a);
        }
        if (typeof b === 'string') {
            b = parseAssert(b);
        }

        const compare = operatorToFnMap[op];
        for (const level of levels) {
            if (a[level] !== b[level]) {
                return compare(a[level], b[level]);
            }
        }
        return compare(a.minor, b.minor);
    }

    export function isCrucialOutdated() {
        return isOutdated(isIOS() ? env.IOS_SHELL_VERSION_CRUCIAL : env.ANDROID_SHELL_VERSION_CRUCIAL);
    }

    export function isOptimalOutdated() {
        return isOutdated(isIOS() ? env.IOS_SHELL_VERSION_OPTIMAL : env.ANDROID_SHELL_VERSION_OPTIMAL);
    }

    function isOutdated(comparedTo: ShellVersion | string) {
        if (typeof comparedTo === 'string') {
            comparedTo = parseAssert(comparedTo);
        }
        const current = getFromWindow();
        if (!current) {
            return null;
        }
        return compare(current, '<', comparedTo);
    }

    if (import.meta.vitest) {
        const {test, expect} = import.meta.vitest;

        test('ShellVersion.compare', () => {
            expect(compare('2025.01.1', '>', '2025.01.0')).toBe(true);
            expect(compare('2025.02.0', '>', '2025.01.99')).toBe(true);
            expect(compare('2024.12.99', '<', '2025.01.0')).toBe(true);
            expect(compare('2025.01.0', '<', '2025.01.0')).toBe(false);
            expect(compare('2025.01.0', '<=', '2025.01.0')).toBe(true);
            expect(compare('2025.01.0', '>=', '2025.01.0')).toBe(true);
        });
    }
}
